import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { format, setHours, setMinutes } from 'date-fns';
import CustomDatePicker from './CustomDatePicker';
import CustomSelect from './CustomSelect';
import RegionsFilter from './RegionsFilter';
import Icon from './Icon';
import {
  getCategoryRowId,
  getCategorySearchHaystack,
  getLocalizedCategoryTitle,
} from '../utils/categoryLabels';
import '../css/sort-menu.css';

const UsersSortFiltersMenu = ({
  sort,
  direction,
  onSortChange,
  onDirectionChange,
  createdFrom,
  createdTo,
  onDateFromChange,
  onDateToChange,
  ratingFrom = '',
  ratingTo = '',
  onRatingFromChange,
  onRatingToChange,
  status,
  statusOptions,
  onStatusChange,
  verified = '',
  verificationOptions = [],
  onVerifiedChange,
  marketingConsent = '',
  marketingConsentOptions = [],
  onMarketingConsentChange,
  id = '',
  onIdChange,
  translationKey = 'users',
  categories,
  selectedCategoryId = '',
  onSelectCategory,
  onClearCategory,
  categoriesLoading,
  categoriesError,
  onRequestCategories,
  selectedRegionCodes = [],
  onRegionCodesChange,
  ordersCount = '',
  ordersCountOrMore = false,
  onOrdersCountChange,
  onOrdersCountOrMoreChange,
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showExtraFilters, setShowExtraFilters] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) setCategorySearch('');
  }, [isOpen]);

  // Закрытие по клику вне меню.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const sortOptions = [
    { value: 'id', label: t(`${translationKey}.filters.id`) },
    { value: 'fullName', label: t(`${translationKey}.filters.sortByFullName`) },
    { value: 'rating', label: t(`${translationKey}.filters.sortByRating`) },
    { value: 'updateDate', label: t(`${translationKey}.filters.sortByDate`) },
    { value: 'createDate', label: t(`${translationKey}.filters.sortByCreatedDate`) },
  ];

  const directionOptions = [
    { value: 'DESC', label: t(`${translationKey}.filters.descending`), icon: '↓' },
    { value: 'ASC', label: t(`${translationKey}.filters.ascending`), icon: '↑' },
  ];

  const normalizeDateStartOfDay = (date) => {
    if (!date) return '';
    return format(setMinutes(setHours(date, 0), 0), "yyyy-MM-dd'T'HH:mm");
  };

  const hasCategorySelection =
    selectedCategoryId != null && String(selectedCategoryId).trim() !== '';

  const filteredCategories = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    const q = categorySearch.trim().toLowerCase();
    const list = q
      ? categories.filter((c) => getCategorySearchHaystack(c).includes(q))
      : categories;
    return [...list].sort((a, b) =>
      getLocalizedCategoryTitle(a, i18n.language, '').localeCompare(
        getLocalizedCategoryTitle(b, i18n.language, ''),
        undefined,
        { sensitivity: 'base' }
      )
    );
  }, [categories, categorySearch, i18n.language]);

  const isSortMenuActive = useMemo(() => {
    const sortNonDefault = sort !== 'createDate' || direction !== 'DESC';
    const extra =
      Boolean(status) ||
      Boolean(createdFrom) ||
      Boolean(createdTo) ||
      Boolean(verified) ||
      Boolean(marketingConsent) ||
      Boolean(ratingFrom) ||
      Boolean(ratingTo) ||
      Boolean(id && String(id).trim()) ||
      hasCategorySelection ||
      (Array.isArray(selectedRegionCodes) && selectedRegionCodes.length > 0) ||
      (ordersCount !== '' && ordersCount != null);
    return sortNonDefault || extra;
  }, [sort, direction, status, createdFrom, createdTo, verified, marketingConsent, ratingFrom, ratingTo, id, hasCategorySelection, selectedRegionCodes, ordersCount]);

  return (
    <div className="sort-menu-wrapper orders-sort-filters-wrapper" ref={menuRef}>
      <button
        type="button"
        className={`sort-menu-button${isSortMenuActive ? ' filter-active' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-label={t(`${translationKey}.filters.sortButton`)}
      >
        <span className="sort-menu-icon">
          <Icon name="sort-arrows" size={14} />
        </span>
        <span className="sort-menu-label">{t(`${translationKey}.filters.sortButton`)}</span>
        <span className="sort-menu-arrow">
          <Icon
            name="chevron-down"
            size={12}
            className={`expand-icon ${isOpen ? 'open' : ''}`}
          />
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="sort-menu-dropdown orders-sort-filters-dropdown"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
          >
            <div className="orders-sort-main-grid">
              <div className="sort-menu-section">
                <div className="sort-menu-section-title">{t(`${translationKey}.filters.sortBy`)}</div>
                <div className="sort-menu-options">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`sort-menu-option ${sort === option.value ? 'active' : ''}`}
                      onClick={() => onSortChange(option.value)}
                    >
                      <span className="sort-menu-option-label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="sort-menu-section">
                <div className="sort-menu-section-title">
                  {sort === 'id'
                    ? t(`${translationKey}.filters.id`)
                    : t(`${translationKey}.filters.sortDirection`)}
                </div>
                <div className="sort-menu-options">
                  {sort === 'id' ? (
                    <div className="sort-menu-input-wrapper">
                      <input
                        type="text"
                        className="sort-menu-text-input"
                        placeholder={t(`${translationKey}.filters.idPlaceholder`)}
                        value={id || ''}
                        onChange={(e) => onIdChange(e.target.value)}
                        style={{
                          border: '2px solid var(--accent-primary)',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          width: '100%',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  ) : sort === 'rating' ? (
                    <div className="sort-menu-rating-column">
                      {directionOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`sort-menu-option ${direction === option.value ? 'active' : ''}`}
                          onClick={() => onDirectionChange(option.value)}
                        >
                          <span className="sort-menu-option-icon">{option.icon}</span>
                          <span className="sort-menu-option-label">{option.label}</span>
                        </button>
                      ))}
                      <div className="sort-menu-rating-range">
                        <div className="sort-menu-rating-field">
                          <span className="sort-menu-rating-label">
                            {t(`${translationKey}.filters.ratingRangeShortFrom`)}
                          </span>
                          <input
                            type="number"
                            min={0}
                            max={5}
                            placeholder="0"
                            className="sort-menu-rating-input"
                            value={ratingFrom === '' || ratingFrom == null ? '' : ratingFrom}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '') {
                                onRatingFromChange('');
                                return;
                              }
                              const n = parseInt(v, 10);
                              if (!Number.isNaN(n)) {
                                const clamped = Math.min(5, Math.max(0, n));
                                onRatingFromChange(String(clamped));
                              }
                            }}
                          />
                        </div>
                        <div className="sort-menu-rating-field">
                          <span className="sort-menu-rating-label">
                            {t(`${translationKey}.filters.ratingRangeShortTo`)}
                          </span>
                          <input
                            type="number"
                            min={0}
                            max={5}
                            placeholder="5"
                            className="sort-menu-rating-input"
                            value={ratingTo === '' || ratingTo == null ? '' : ratingTo}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '') {
                                onRatingToChange('');
                                return;
                              }
                              const n = parseInt(v, 10);
                              if (!Number.isNaN(n)) {
                                const clamped = Math.min(5, Math.max(0, n));
                                onRatingToChange(String(clamped));
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    directionOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`sort-menu-option ${direction === option.value ? 'active' : ''}`}
                        onClick={() => onDirectionChange(option.value)}
                      >
                        <span className="sort-menu-option-icon">{option.icon}</span>
                        <span className="sort-menu-option-label">{option.label}</span>
                      </button>
                    ))
                  )}
                </div>

                {sort === 'createDate' && (
                  <div className="orders-sort-date-range">
                    <div className="orders-sort-date-range-grid">
                      <div className="more-filters-item">
                        <label className="sort-menu-section-title orders-date-inline-title">
                          {t(`${translationKey}.filters.dateFrom`)}
                        </label>
                        <CustomDatePicker
                          selected={createdFrom ? new Date(createdFrom) : null}
                          showTimeSelect={false}
                          onChange={(date) => onDateFromChange(normalizeDateStartOfDay(date))}
                        />
                      </div>

                      <div className="more-filters-item">
                        <label className="sort-menu-section-title orders-date-inline-title">
                          {t(`${translationKey}.filters.dateTo`)}
                        </label>
                        <CustomDatePicker
                          selected={createdTo ? new Date(createdTo) : null}
                          showTimeSelect={false}
                          onChange={(date) => onDateToChange(normalizeDateStartOfDay(date))}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="extra-filters-accordion">
              <button
                type="button"
                className="orders-extra-filters-toggle"
                onClick={() => {
                  const next = !showExtraFilters;
                  setShowExtraFilters(next);
                  if (next && onRequestCategories) onRequestCategories();
                }}
                aria-expanded={showExtraFilters}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="sliders" size={15} />
                  {t('orders.filters.extraFilters')}
                </span>
                <span className="orders-extra-filters-toggle-icon">
                  <Icon name="chevron-down" size={16} />
                </span>
              </button>

              <div className={`orders-extra-filters-body ${showExtraFilters ? 'open' : ''}`}>
                <div>
                  <div className="more-filters-content users-extra-filters-grid">
                    <div className="more-filters-item">
                      <label className="more-filters-label">{t(`${translationKey}.filters.status`)}</label>
                      <CustomSelect
                        value={status || ''}
                        options={statusOptions}
                        onChange={onStatusChange}
                        minWidth="100%"
                        hasActiveFilter={Boolean(status)}
                      />
                    </div>

                    {onVerifiedChange && (
                      <div className="more-filters-item">
                        <label className="more-filters-label">{t(`${translationKey}.filters.verification`)}</label>
                        <CustomSelect
                          value={verified || ''}
                          options={verificationOptions}
                          onChange={onVerifiedChange}
                          minWidth="100%"
                          hasActiveFilter={Boolean(verified)}
                        />
                      </div>
                    )}

                    {onMarketingConsentChange && (
                      <div className="more-filters-item">
                        <label className="more-filters-label">{t(`${translationKey}.filters.marketingConsent`)}</label>
                        <CustomSelect
                          value={marketingConsent || ''}
                          options={marketingConsentOptions}
                          onChange={onMarketingConsentChange}
                          minWidth="100%"
                          hasActiveFilter={Boolean(marketingConsent)}
                        />
                      </div>
                    )}

                    {onOrdersCountChange && (
                      <div className="more-filters-item">
                        <label className="more-filters-label">
                          {t(`${translationKey}.filters.ordersCount`)}
                        </label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="number"
                            min={0}
                            placeholder={t(`${translationKey}.filters.ordersCountPlaceholder`)}
                            className="filter-input"
                            style={{ flex: 1, minWidth: 0 }}
                            value={ordersCount === '' || ordersCount == null ? '' : ordersCount}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '') {
                                onOrdersCountChange('');
                                return;
                              }
                              const n = parseInt(v, 10);
                              if (!Number.isNaN(n)) {
                                onOrdersCountChange(String(Math.max(0, n)));
                              }
                            }}
                          />
                          {onOrdersCountOrMoreChange && (
                            <button
                              type="button"
                              className={`sort-menu-option ${ordersCountOrMore ? 'active' : ''}`}
                              onClick={() => onOrdersCountOrMoreChange(!ordersCountOrMore)}
                              style={{ whiteSpace: 'nowrap', width: 'auto', flex: '0 0 auto' }}
                            >
                              {t(`${translationKey}.filters.ordersCountOrMore`)}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {categories !== undefined && onSelectCategory && (
                      <div className="more-filters-item more-filters-categories-block">
                        <div className="more-filters-categories-header">
                          <label className="more-filters-label">
                            {t(`${translationKey}.filters.providerCategories`)}
                          </label>
                          {hasCategorySelection && onClearCategory && (
                            <button
                              type="button"
                              className="more-filters-categories-clear"
                              onClick={onClearCategory}
                            >
                              {t(`${translationKey}.filters.categoriesClearSelected`)}
                            </button>
                          )}
                        </div>
                        <p className="more-filters-categories-hint">
                          {t(`${translationKey}.filters.providerCategoriesHint`)}
                        </p>
                        {categoriesLoading ? (
                          <div className="more-filters-categories-status">
                            {t(`${translationKey}.filters.categoriesLoading`)}
                          </div>
                        ) : categoriesError ? (
                          <div className="more-filters-categories-status more-filters-categories-error">
                            {t(`${translationKey}.filters.categoriesError`)}
                          </div>
                        ) : (
                          <>
                            <input
                              type="search"
                              className="sort-menu-text-input more-filters-categories-search"
                              value={categorySearch}
                              onChange={(e) => setCategorySearch(e.target.value)}
                              placeholder={t(`${translationKey}.filters.categoriesSearchPlaceholder`)}
                              style={{
                                border: '2px solid var(--accent-primary)',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                width: '100%',
                                background: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                                fontSize: '0.9rem',
                              }}
                            />
                            {hasCategorySelection && (
                              <div className="more-filters-category-chips" aria-live="polite">
                                {(() => {
                                  const id = String(selectedCategoryId);
                                  const cat = categories.find((c) => getCategoryRowId(c) === id);
                                  const label = cat
                                    ? getLocalizedCategoryTitle(cat, i18n.language, id)
                                    : id;
                                  return (
                                    <button
                                      type="button"
                                      className="more-filters-category-chip"
                                      onClick={onClearCategory}
                                      title={t(`${translationKey}.filters.categoriesRemoveChip`)}
                                    >
                                      <span className="more-filters-category-chip-label">{label}</span>
                                      <span className="more-filters-category-chip-x" aria-hidden>
                                        ×
                                      </span>
                                    </button>
                                  );
                                })()}
                              </div>
                            )}
                            <div
                              className="more-filters-categories-list"
                              role="radiogroup"
                              aria-label={t(`${translationKey}.filters.providerCategories`)}
                            >
                              {filteredCategories.length === 0 ? (
                                <div className="more-filters-categories-status">
                                  {t(`${translationKey}.filters.categoriesEmpty`)}
                                </div>
                              ) : (
                                filteredCategories.map((c) => {
                                  const idStr = getCategoryRowId(c);
                                  if (!idStr) return null;
                                  const checked = String(selectedCategoryId) === idStr;
                                  const rowTitle = getLocalizedCategoryTitle(
                                    c,
                                    i18n.language,
                                    idStr
                                  );
                                  return (
                                    <label key={idStr} className="more-filters-category-row">
                                      <input
                                        type="radio"
                                        name="users-sort-filters-category"
                                        checked={checked}
                                        onChange={() => onSelectCategory(idStr)}
                                      />
                                      <span className="more-filters-category-title">
                                        {rowTitle}
                                      </span>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {onRegionCodesChange && (
                      <RegionsFilter
                        selected={selectedRegionCodes}
                        onChange={onRegionCodesChange}
                        translationKey={translationKey}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UsersSortFiltersMenu;

